/**
 * SavedTablePage — read-only viewer for a table saved from a chat answer.
 *
 * Route: /app/data/saved/:id
 * Reads from savedTablesStore by ID — no backend call needed.
 */
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Trash2, Maximize2, Pencil, Check, X } from "lucide-react";
import { useSavedTablesStore } from "../../stores/savedTablesStore";
import { confirm } from "../../components/ConfirmDialog";
import TableFullscreenModal from "../../components/TableFullscreenModal";
import { toast } from "../../stores/toastStore";
import { useNavigate } from "react-router-dom";

export default function SavedTablePage() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const getTable    = useSavedTablesStore((s) => s.getTable);
  const deleteTable = useSavedTablesStore((s) => s.deleteTable);
  const renameTable = useSavedTablesStore((s) => s.renameTable);

  const table = getTable(id);

  const [editing,    setEditing]    = useState(false);
  const [nameValue,  setNameValue]  = useState(table?.name || "");
  const [fullscreen, setFullscreen] = useState(false);

  if (!table) {
    return (
      <div className="px-8 lg:px-12 pt-12 pb-16">
        <Link
          to="/app/data"
          className="inline-flex items-center gap-1.5 mb-6 hover:text-[var(--color-ink)] transition-colors"
          style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-ink-3)" }}
        >
          <ArrowLeft size={11} /> Data
        </Link>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-3)" }}>
          This saved table no longer exists.
        </p>
      </div>
    );
  }

  const { columns, rows, sourceQuery, savedAt } = table;

  async function handleDelete() {
    const ok = await confirm({
      title: "Delete this saved table?",
      description: "This will remove it from My Tables permanently.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    deleteTable(id);
    toast.success("Saved table deleted");
    navigate("/app/data");
  }

  function commitRename() {
    const name = nameValue.trim();
    if (name && name !== table.name) {
      renameTable(id, name);
      toast.success("Table renamed");
    }
    setEditing(false);
  }

  function handleRenameKey(e) {
    if (e.key === "Enter")  { e.preventDefault(); commitRename(); }
    if (e.key === "Escape") { setEditing(false); setNameValue(table.name); }
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="shrink-0 px-8 lg:px-12 pt-8 pb-4 border-b border-[var(--color-rule)] flex items-end justify-between gap-4">
        <div className="min-w-0">
          <Link
            to="/app/data"
            className="reveal inline-flex items-center gap-1.5 mb-4 hover:text-[var(--color-ink)] transition-colors"
            style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-ink-3)" }}
          >
            <ArrowLeft size={11} /> Data
          </Link>

          {/* Editable name */}
          <div className="reveal flex items-baseline gap-3" style={{ animationDelay: "80ms" }}>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={handleRenameKey}
                  onBlur={commitRename}
                  className="display border-b border-[var(--color-accent)] outline-none bg-transparent"
                  style={{ fontSize: "var(--text-2xl)" }}
                />
                <button onClick={commitRename} className="p-1 text-[var(--color-status-even)]">
                  <Check size={14} />
                </button>
                <button onClick={() => { setEditing(false); setNameValue(table.name); }} className="p-1 text-[var(--color-ink-3)]">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-baseline gap-2">
                <h1 className="display" style={{ fontSize: "var(--text-2xl)" }}>{table.name}</h1>
                <button
                  onClick={() => { setEditing(true); setNameValue(table.name); }}
                  className="p-1 text-[var(--color-ink-3)] hover:text-[var(--color-ink)] transition-colors"
                  title="Rename table"
                >
                  <Pencil size={12} />
                </button>
              </div>
            )}

            <span
              className="inline-flex items-center border border-[var(--color-rule-strong)] px-2 py-0.5 shrink-0"
              style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-ink-3)" }}
            >
              SAVED FROM CHAT
            </span>
          </div>

          {sourceQuery && (
            <p
              className="reveal mt-1"
              style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)", animationDelay: "140ms" }}
            >
              Query: {sourceQuery}
            </p>
          )}
          <p
            className="reveal mt-0.5"
            style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)", animationDelay: "160ms" }}
          >
            {rows.length.toLocaleString()} rows · {columns.length} columns ·{" "}
            Saved {formatDate(savedAt)}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 mb-0.5">
          <button
            onClick={() => setFullscreen(true)}
            className="btn btn-ghost"
            style={{ fontSize: "var(--text-xs)", paddingBlock: "5px" }}
          >
            <Maximize2 size={13} />
            Full screen
          </button>
          <button
            onClick={handleDelete}
            className="btn btn-ghost"
            style={{ fontSize: "var(--text-xs)", paddingBlock: "5px", color: "var(--color-status-over)" }}
          >
            <Trash2 size={13} />
            Delete
          </button>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto min-h-0">
        {rows.length === 0 ? (
          <div className="px-8 pt-8" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-3)" }}>
            No rows in this saved table.
          </div>
        ) : (
          <table
            className="w-full border-collapse"
            style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}
          >
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2.5 text-left border-b border-r border-[var(--color-rule)] bg-[var(--color-paper)] whitespace-nowrap font-medium"
                    style={{ position: "sticky", top: 0, zIndex: 10, fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--color-ink-3)" }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={i % 2 !== 0 ? "bg-[var(--color-paper-2)]" : ""}>
                  {columns.map((col) => (
                    <td
                      key={col}
                      className="border-b border-r border-[var(--color-rule)] px-3 py-2"
                    >
                      {row[col] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Full-screen modal (no save option — already saved) */}
      <TableFullscreenModal
        open={fullscreen}
        columns={columns}
        rows={rows}
        sourceQuery={sourceQuery}
        onClose={() => setFullscreen(false)}
        onSave={null}
      />
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "";
  }
}
